import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ConfigCheckReport,
  ConfigSettingResult,
  ConfigSeverity,
  ConfigStatus,
  PlatformConfigCheck
} from '../models/config-check.model';

/**
 * Deployment config check.
 *
 * Two halves, because they can only be answered from two places:
 *
 *  - {@link load} asks the account API, which fans out to every other service. A service's own
 *    configuration is visible only inside that process, so nothing else can report on it, and the
 *    shared secret those calls carry stays server-side — the browser never holds it.
 *  - {@link checkAdminPortal} checks THIS app's compiled-in environment, which is the one thing the
 *    account API cannot see: it is baked into the bundle at build time, so the only way to know what
 *    a deployed portal is actually pointed at is to ask the running bundle.
 */
@Injectable({ providedIn: 'root' })
export class ConfigCheckService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/admin/config-check';

  constructor(private http: HttpClient) { }

  /** Every service's verdict, plus the checks that span services. */
  load(): Observable<PlatformConfigCheck> {
    return this.http.get<PlatformConfigCheck>(this.baseUrl);
  }

  /**
   * This portal's own build-time environment, checked in the browser and shaped like any other
   * service's report so it renders in the same list.
   *
   * Worth checking rather than assuming: these values are frozen into the bundle at build time, so
   * a portal deployed from the wrong configuration keeps pointing at another environment's APIs
   * until somebody notices the data looks wrong.
   */
  checkAdminPortal(): ConfigCheckReport {
    const env = environment as Record<string, unknown>;
    const deployed = env['production'] === true;

    const settings: ConfigSettingResult[] = [
      this.url(env, 'subscriptionApiUrl', 'Required', deployed,
        'Account API origin. Every page in this portal talks to it; the wrong environment here shows another environment\'s businesses, users and orders.'),
      this.url(env, 'retailApiUrl', 'Required', deployed,
        'Retail POS API origin, used by the marketplace order and product screens.'),
      this.url(env, 'inventoryApiUrl', 'Required', deployed,
        'Inventory API origin, used by the image-derivative console.'),
      this.url(env, 'identityIssuerUrl', 'Required', deployed,
        'IdentityServer authority this portal signs in against. Must be the same issuer the APIs validate tokens from, or every call comes back 401.'),
      this.url(env, 'redirectUri', 'Required', deployed,
        'Sign-in redirect. Must be registered on this portal\'s IdentityServer client or the login loops.'),
      this.url(env, 'postLogoutRedirectUri', 'Required', deployed,
        'Sign-out redirect. Must also be registered on the client.'),
      this.url(env, 'imageUrl', 'Required', deployed,
        'Blob storage root that product and category images are loaded from.'),
      this.text(env, 'clientId', 'Required',
        'This portal\'s IdentityServer client id. A local client id shipped to a deployed portal fails at sign-in.'),
      this.text(env, 'scope', 'Required',
        'Scopes requested at sign-in. A missing scope shows up as 403 on one screen rather than as a login failure.'),
      this.guid(env, 'marketplaceBusinessId', 'Required',
        'Marketplace business the portal acts on. The wrong value reads another business\'s marketplace data.'),
      this.guid(env, 'marketplaceTerminalId', 'Required',
        'Terminal marketplace actions are recorded against.'),
      this.publishableKey(env, 'stripePublishableKey', deployed,
        'Stripe publishable key. A test key in a deployed portal takes no real payments.'),
      this.publishableKey(env, 'paystackPublicKey', deployed,
        'Paystack public key. A test key in a deployed portal takes no real payments.'),
      this.bool(env, 'requireHttps', deployed,
        'Whether the OIDC client insists on https metadata. Should be true anywhere but local development.')
    ];

    return {
      service: 'Admin portal (this app)',
      environment: deployed ? 'Production build' : 'Development build',
      status: this.rollUp(settings),
      settings,
      reachable: true,
      configured: true,
      checkedAtUtc: new Date().toISOString()
    };
  }

  /** Worst outcome wins, matching how the services roll their own settings up. */
  private rollUp(settings: ConfigSettingResult[]): 'Ok' | 'Warning' | 'Error' {
    let worst: 'Ok' | 'Warning' = 'Ok';
    for (const setting of settings) {
      if (setting.status === 'Ok') { continue; }
      if (setting.severity === 'Required' && setting.status !== 'Warning') { return 'Error'; }
      worst = 'Warning';
    }
    return worst;
  }

  private base(
    key: string, severity: ConfigSeverity, description: string, kind: string
  ): ConfigSettingResult {
    return { key, severity, kind, status: 'Ok', secret: false, description };
  }

  private read(env: Record<string, unknown>, key: string): string {
    const value = env[key];
    return value === undefined || value === null ? '' : String(value).trim();
  }

  /** Shared "is it even set?" step; returns null when the caller should stop. */
  private missing(
    result: ConfigSettingResult, value: string
  ): ConfigSettingResult | null {
    if (value !== '') { return null; }
    result.status = 'Missing';
    result.detail = 'Not set in this build\'s environment file.';
    return result;
  }

  private text(
    env: Record<string, unknown>, key: string, severity: ConfigSeverity, description: string
  ): ConfigSettingResult {
    const result = this.base(key, severity, description, 'Text');
    const value = this.read(env, key);
    const missing = this.missing(result, value);
    if (missing) { return missing; }

    result.value = value;
    return result;
  }

  private url(
    env: Record<string, unknown>, key: string, severity: ConfigSeverity, deployed: boolean, description: string
  ): ConfigSettingResult {
    const result = this.base(key, severity, description, 'Url');
    const value = this.read(env, key);
    const missing = this.missing(result, value);
    if (missing) { return missing; }

    result.value = value;

    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      result.status = 'Invalid';
      result.detail = 'Not an absolute URL.';
      return result;
    }

    // A localhost origin in a production build is the single most common way a portal ends up
    // pointed at nothing at all, and it is invisible until a page fails to load.
    if (deployed && /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(parsed.hostname)) {
      result.status = 'Warning';
      result.detail = 'Points at a local address, which cannot resolve from a deployed portal.';
      return result;
    }

    if (deployed && parsed.protocol === 'http:') {
      result.status = 'Warning';
      result.detail = 'Uses http rather than https.';
    }

    return result;
  }

  private guid(
    env: Record<string, unknown>, key: string, severity: ConfigSeverity, description: string
  ): ConfigSettingResult {
    const result = this.base(key, severity, description, 'Guid');
    const value = this.read(env, key);
    const missing = this.missing(result, value);
    if (missing) { return missing; }

    result.value = value;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      result.status = 'Invalid';
      result.detail = 'Not a GUID.';
    }
    return result;
  }

  /**
   * Publishable payment keys are safe to show — they are meant to reach the browser. What matters
   * is which mode they are in: a test key in a production build accepts card details and takes no
   * money, and nothing about the page looks wrong while it happens.
   */
  private publishableKey(
    env: Record<string, unknown>, key: string, deployed: boolean, description: string
  ): ConfigSettingResult {
    const result = this.base(key, 'Recommended', description, 'Text');
    const value = this.read(env, key);
    const missing = this.missing(result, value);
    if (missing) { return missing; }

    result.value = value.length > 16 ? value.slice(0, 16) + '…' : value;

    if (deployed && /^(pk_test_|pk_test)/i.test(value)) {
      result.status = 'Warning';
      result.detail = 'This is a TEST key in a production build — no real payment will be taken.';
    }
    return result;
  }

  private bool(
    env: Record<string, unknown>, key: string, deployed: boolean, description: string
  ): ConfigSettingResult {
    const result = this.base(key, 'Recommended', description, 'Boolean');
    const value = env[key];

    if (typeof value !== 'boolean') {
      result.status = 'Missing';
      result.detail = 'Not set in this build\'s environment file.';
      return result;
    }

    result.value = String(value);
    if (deployed && value === false) {
      result.status = 'Warning';
      result.detail = 'False in a production build permits plaintext OIDC metadata.';
    }
    return result;
  }
}

/** Re-exported so the page can narrow on statuses without importing two modules. */
export type { ConfigStatus };
